import { HttpClient } from '@angular/common/http';
import {
  BehaviorSubject,
  Observable,
  Subject,
  map,
  of,
  takeUntil,
  tap,
} from 'rxjs';
import { RequestParam } from '@t-llevo/util';
import { AuthBasicData } from '../models/auth-basic';

export abstract class AuthBase<T, R> {
  private stop$ = new Subject<void>();
  private authSubject$ = new BehaviorSubject<AuthBasicData<T> | null>(null);
  private refreshSubject$ = new BehaviorSubject<string | null>(null);
  private isRefreshingToken = false;

  sessionData$ = this.authSubject$.asObservable().pipe(takeUntil(this.stop$));
  refresh$ = this.refreshSubject$.asObservable().pipe(takeUntil(this.stop$));
  sessionData: AuthBasicData<T> | null = null;
  constructor(public http: HttpClient) {
    const localStorageTmp = localStorage.getItem('sessionData');
    /* OBTENER LA SESSION ACTIVA DESDE LOCALSTORAGE EN CASO DE RECARGA DEL BROWSER */
    if (localStorageTmp) {
      this.sessionData = JSON.parse(localStorageTmp);
      this.authSubject$.next(this.sessionData);
      this.refreshSubject$.next(this.sessionData?.access ?? 'NO_TOKEN');
    }
  }

  get isRefreshing(): boolean {
    return this.isRefreshingToken;
  }

  set isRefreshing(val: boolean) {
    this.isRefreshingToken = val;
  }

  stop() {
    this.stop$.next();
    this.stop$.complete();
  }

  login<D>(reqInfo: RequestParam, requestData: D) {
    const { url, ...rest } = reqInfo;
    this.isRefreshingToken = true;
    return this.http.post<R>(url, requestData, { ...rest }).pipe(
      tap((authUserInfo: R) => {
        this.sessionData = this._transfom(authUserInfo);
        localStorage.setItem('sessionData', JSON.stringify(this.sessionData));
        if (authUserInfo) {
          this.isRefreshingToken = false;
          this.authSubject$.next(this.sessionData);
          this.refreshSubject$.next(this.sessionData?.access ?? 'NO_TOKEN');
        }
      }),
      takeUntil(this.stop$)
    );
  }

  logout<K, O>(reqInfo: RequestParam, data: K, rta: O): Observable<O> {
    if (this.sessionData) {
      return this.http
        .post<O>(reqInfo.url, data, {
          headers: reqInfo.headers,
          params: reqInfo.params,
        })
        .pipe(
          tap(() => {
            this.localLogOut();
          })
        );
    }
    return of(rta);
  }

  localLogOut(): void {
    localStorage.removeItem('sessionData');
    this.sessionData = null;
    sessionStorage.clear();
    localStorage.clear();
    this.authSubject$.next(null);
    this.stop();
  }

  getAuthData(): AuthBasicData<T> | null {
    return this.sessionData ?? null;
  }

  getAuthToken(): string | null | undefined {
    return this.sessionData ? this.sessionData.access : null;
  }

  getRefreshToken(): string | null | undefined {
    return this.sessionData ? this.sessionData.refresh : null;
  }

  verify(): boolean {
    if (
      this.sessionData &&
      this.sessionData.access &&
      this.sessionData.access !== 'NO_TOKEN'
    ) {
      return true;
    }
    return false;
  }

  changePassword<O>(reqInfo: RequestParam, data: O) {
    const { url, ...rest } = reqInfo;
    return this.http.put(url, data, { ...rest });
  }

  refreshToken<D>(
    reqInfo: RequestParam,
    data: D
  ): Observable<AuthBasicData<T>> {
    this.isRefreshingToken = true;
    const { url, ...rest } = reqInfo;
    return this.http.post<R>(url, data, { ...rest }).pipe(
      map((data) => this._transfom(data)),
      tap((data) => {
        localStorage.setItem('sessionData', JSON.stringify(data));
        this.sessionData = { ...data };
        this.authSubject$.next(this.sessionData);
        this.refreshSubject$.next(this.sessionData?.access ?? 'NO_TOKEN');
        this.isRefreshingToken = false;
      })
    );
  }

  protected abstract _transfom(input: R): AuthBasicData<T>;
}
